import React, { memo, useState } from "react";
import { Handle, Position } from "@xyflow/react";

import JobConfigration from "../../BuilderComponents/JobConfigration/JobConfigration";
import ButtonActionIcon from "../../BuilderComponents/ButtonActionIcon";
import StateOrActivityChangeLogic from "./StateChangeLogic";
import MaterialIcon from "../../BuilderComponents/MaterialIcon";

const NodeHeaderMoveAction = ({ direction, onClick }) => (
  <button
    onClick={onClick}
    style={{
      background: "none",
      border: "none",
      cursor: "pointer",
      color: "#64748b",
      borderRadius: "4px",
      padding: "2px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "all 0.2s ease"
    }}
    onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
    onMouseLeave={(e) => (e.currentTarget.style.opacity = 0.7)}
  >
    <MaterialIcon
      name={direction === "up" ? "arrow_upward" : "arrow_downward"}
      title={`move ${direction}`}
      style={{ fontSize: "24px" }}
    />
  </button>
);

export const BaseNode = ({ selected, children, hovered, currentTab }) => {
  const isJobNode = currentTab === "Job Node";

  return (
    <div
      style={{
        border: selected ? "2px solid #3b82f6" : hovered ? "1px solid #3b82f6" : "1px solid #e2e8f0",
        background: "white",
        borderRadius: "8px",
        padding: "12px",
        position: "relative",
        boxShadow: selected
          ? "0 4px 12px rgba(59, 130, 246, 0.15)"
          : hovered
            ? "0 2px 8px rgba(0, 0, 0, 0.05)"
            : "none",
        transition: "all 0.2s ease",
        width: isJobNode ? "fit-content" : "400px",
        minWidth: "200px"
      }}
    >
      {children}
    </div>
  );
};

const createPerimeterHandles = (nodeId, selected, hovered) => {
  const handleStyle = {
    opacity: hovered || selected ? 1 : 0,
    background: "black",
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    transition: "opacity 0.2s ease"
  };

  return (
    <>
      {/* Corners */}
      <Handle type="source" position={Position.TopLeft} id={`${nodeId}-source-topleft`} style={handleStyle} />
      <Handle type="target" position={Position.TopLeft} id={`${nodeId}-target-topleft`} style={handleStyle} />

      <Handle type="source" position={Position.TopRight} id={`${nodeId}-source-topright`} style={handleStyle} />
      <Handle type="target" position={Position.TopRight} id={`${nodeId}-target-topright`} style={handleStyle} />

      <Handle type="source" position={Position.BottomLeft} id={`${nodeId}-source-bottomleft`} style={handleStyle} />
      <Handle type="target" position={Position.BottomLeft} id={`${nodeId}-target-bottomleft`} style={handleStyle} />

      <Handle type="source" position={Position.BottomRight} id={`${nodeId}-source-bottomright`} style={handleStyle} />
      <Handle type="target" position={Position.BottomRight} id={`${nodeId}-target-bottomright`} style={handleStyle} />

      {/* Midpoints */}
      <Handle type="source" position={Position.Top} id={`${nodeId}-source-top`} style={handleStyle} />
      <Handle type="target" position={Position.Top} id={`${nodeId}-target-top`} style={handleStyle} />

      <Handle type="source" position={Position.Right} id={`${nodeId}-source-right`} style={handleStyle} />
      <Handle type="target" position={Position.Right} id={`${nodeId}-target-right`} style={handleStyle} />

      <Handle type="source" position={Position.Bottom} id={`${nodeId}-source-bottom`} style={handleStyle} />
      <Handle type="target" position={Position.Bottom} id={`${nodeId}-target-bottom`} style={handleStyle} />

      <Handle type="source" position={Position.Left} id={`${nodeId}-source-left`} style={handleStyle} />
      <Handle type="target" position={Position.Left} id={`${nodeId}-target-left`} style={handleStyle} />
    </>
  );
};

const CustomNode = memo(({ id, selected, data }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <div onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      <BaseNode selected={selected} hovered={hovered} currentTab={data.currentTab}>
        {/* Perimeter handles */}
        {createPerimeterHandles(id, selected, hovered)}

        {data.currentTab === "Job Node" ? (
          <div onClick={() => data.editJob(data)}>
            <JobConfigration
              key={data.activity.id}
              editJob={data.activity}
              smId={data.stateId}
              actId={data.actId}
              onClick={data.editJobTriggerData}
              templateObject={data.templateObject}
              allJobs={data.allJobs}
              isSmJob={data.isSmJob}
              isJobIconAndNameOnly={true}
            />
          </div>
        ) : ["state", "activity"].includes(data.parent) ? (
          <div className="job_single_card w_100 ">
            <StateOrActivityChangeLogic
              logic={data.activity}
              setLogic={data.setFunction}
              selectionObj={data.selectionObj}
              type={data.parent}
              editJob={() => data.editJob(data)}
              deleteJob={() => data.deleteJob(data, id)}
              index={data.index}
            />
          </div>
        ) : (
          <>
            <div
              className={`position_absolute border_full border_radius_medium p_1 d_flex gap_1 justify_end ${hovered ? "" : "d_none"}`}
              style={{ right: "10px", top: "-20px", zIndex: "10", background: "white" }}
            >
              <div
                style={{
                  backgroundColor: "#e0e7ff",
                  color: "#4f46e5",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  fontSize: "14px",
                  fontWeight: "500",
                  display: "flex",
                  alignItems: "center",
                  width: "fit-content"
                }}
              >
                {data.index}
              </div>

              {data.showMove && data.index !== 0 && (
                <div>
                  <NodeHeaderMoveAction direction="up" onClick={() => data.moveNode("up", data)} />
                </div>
              )}

              {data.showMove && !data.isLastElement && (
                <div>
                  <NodeHeaderMoveAction direction="down" onClick={() => data.moveNode("down", data)} />
                </div>
              )}
            </div>

            <div className="d_flex gap_1" style={{ position: "relative" }}>
              <div className="job_single_card w_100 overflow_hidden">
                <div className="overlay_cover"></div>
                <div className="job_action_btn ">
                  <ButtonActionIcon size="sm" type="edit" onClick={() => data.editJob(data)} />
                  <ButtonActionIcon size="sm" type="delete" onClick={() => data.deleteJob(data, id)} />
                </div>
                <JobConfigration
                  key={data.activity.id}
                  editJob={data.activity}
                  smId={data.stateId}
                  actId={data.actId}
                  onClick={data.editJobTriggerData}
                  templateObject={data.templateObject}
                  isReadOnly={data.isReadOnly}
                  allJobs={data.allJobs}
                  isSmJob={data.isSmJob}
                />
              </div>
            </div>
          </>
        )}
      </BaseNode>
    </div>
  );
});

export default CustomNode;
